import {ControlledComponent} from 'panel';
import {registerMPElement} from '../../util/register-element';
import {filterAttributes} from '../../util';
import {getModifierKeys, NAVIGATION_KEY_CODES} from '../../util/dom';
import {ItemsMenuController} from './controller';

import css from './index.styl?inline';
import template from './index.jade';

class ItemsMenu extends ControlledComponent {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'empty-message': `string`,
      'empty-search-results-message': {
        type: `string`,
        default: `Your search returned no results`,
        description: `Message to show when a filter results in an empty list`,
      },
      multiple: {type: `boolean`, default: false, description: `set "true" to render multi-checkbox selection`},
      progressive: {type: `boolean`, default: true},
      'search-exact': {type: `boolean`, description: `only match search-filter substrings, don't fuzzy-match`},
      'search-filter': `string`,
      sections: {type: `json`, default: []},
      'sections-hint': `string`,
      'show-select-all': `boolean`,
    };
  }

  createController() {
    return new ItemsMenuController({
      dispatchCustomEvent: (name, opts) => this.dispatchEvent(new CustomEvent(name, opts)),
    });
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template,
      controller: this.createController(),
      helpers: {
        filterAttributes,

        handleClickPill: (item, ev) => {
          // Pill click automatically bubbles up and becomes item click. We don't want that
          if (ev) {
            ev.stopPropagation();
          }

          this.controller.handleClickPill(item, getModifierKeys(ev));
        },
        handleClickItem: (item, ev) => this.controller.handleClickItem(item, getModifierKeys(ev)),
      },
    };
  }

  constructor() {
    super();
    this._keydownProxyEl = null;
    // Since handlers can be called on proxy elements, use bind for consistent `this`
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    if (this.initializing || this.initialized) {
      return;
    }

    super.connectedCallback();
    this.addEventListener(`scroll`, this.handleScroll);
    this.addEventListener(`keydown`, this.handleKeydown);
  }

  disconnectedCallback() {
    if (!this.initialized) {
      return;
    }

    super.disconnectedCallback();
    this.removeEventListener(`scroll`, this.handleScroll);
    this.removeEventListener(`keydown`, this.handleKeydown);
    this.keydownProxyEl = null;
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (attr in this._attrsSchema) {
      this.controller.handleAttributeChange(attr, this.attr(attr));
    }

    // Searching resets focus to first item which the user is most likely to select
    if (attr === `search-filter` && this.controller.getFocusedItemIndex() > 0) {
      this.controller.focusItem(0);
      window.requestAnimationFrame(() => this.scrollToItemIfNeeded());
    }
  }

  /**
   * @param {HTMLElement} domEl
   *  Handles arrow and tab keydown events from domEl for keyboard accessibility
   */
  set keydownProxyEl(domEl) {
    if (this._keydownProxyEl) {
      this._keydownProxyEl.removeEventListener(`keydown`, this.handleKeydown);
    }
    this._keydownProxyEl = domEl;
    if (this._keydownProxyEl) {
      this._keydownProxyEl.addEventListener(`keydown`, this.handleKeydown);
    }
  }

  /**
   * @param {(input: string) => Section} sectionFromInputFn
   *    function that returns a new section to be added depending on user input */
  set sectionFromInputFn(sectionFromInputFn) {
    this.controller._update({sectionFromInputFn});
  }

  /**
   * @param sectionsVal
   *  sections passed as a prop for perf purposes when passing large number of items.
   *  JSON serialization/deserialization can have a noticeable perf impact.
   */
  set sections(sectionsVal) {
    this.controller.handleAttributeChange(`sections`, sectionsVal);
  }

  handleKeydown(ev) {
    const keyCode = ev.keyCode;
    if (Object.values(NAVIGATION_KEY_CODES).includes(keyCode)) {
      if (keyCode === NAVIGATION_KEY_CODES.TAB) {
        // We should never be swallowing tab presses since they are used by browser to switch focus
        // however to preserve backwards compatibility, we only swallow tab presses if item has pill
        const focusedItem = this.controller.getFocusedItem();
        if (focusedItem && focusedItem.hasPill) {
          ev.stopPropagation();
          ev.preventDefault();
        }
      } else {
        ev.stopPropagation();
        ev.preventDefault();
      }
    }

    this.controller.handleKeydown(keyCode, getModifierKeys(ev));
    if (keyCode === NAVIGATION_KEY_CODES.UP_ARROW || keyCode === NAVIGATION_KEY_CODES.DOWN_ARROW) {
      window.requestAnimationFrame(() => {
        this.scrollToItemIfNeeded();
      });
    }
  }

  handleSearchInput(ev) {
    this.controller.handleAttributeChange(`search-filter`, ev.target.value);
  }

  handleScroll(ev) {
    const PROGRESSIVE_LIST_BUFFER_PX = 250;
    const scrollBottom = ev.target.scrollTop + ev.target.offsetHeight;
    if (scrollBottom + PROGRESSIVE_LIST_BUFFER_PX >= ev.target.scrollHeight) {
      this.controller.increaseProgressiveListSize();
    }
    this.showScrolledTopShadow(ev.target.scrollTop > 0);
  }

  showScrolledTopShadow(showScrollShadow) {
    if (this.showScrollShadow !== showScrollShadow) {
      // top shadow is a dynamic host style, therefore we need to use dom style api
      this.style.boxShadow = showScrollShadow ? `inset 0px 6px 10px -10px #6d859e` : `none`;
      this.showScrollShadow = showScrollShadow;
    }
  }

  scrollToItemIfNeeded() {
    const listEl = this;
    const focusedEl = this.el.querySelector(`.list-option.focused`);
    if (!focusedEl) {
      return;
    }

    // The top threshold should be large enough to account for section header
    const THRESHOLD = 45;
    const listElTop = listEl.scrollTop;
    const listElBottom = listElTop + listEl.offsetHeight;
    const focusedElTop = focusedEl.offsetTop - listEl.offsetTop;
    const focusedElBottom = focusedElTop + focusedEl.offsetHeight;
    if (focusedElTop - THRESHOLD < listElTop) {
      listEl.scrollTop = focusedElTop - THRESHOLD;
    } else if (focusedElBottom + THRESHOLD > listElBottom) {
      listEl.scrollTop = focusedElBottom + THRESHOLD - listEl.offsetHeight;
    }
  }

  /** For mnemonic focus. Find item with label that matches prefix and focus on it */
  focusItemWithPrefix(prefix) {
    prefix = prefix.toLowerCase();
    const sections = this.controller.getDisplaySections();
    for (const section of sections) {
      if (section.items) {
        for (const item of section.items) {
          if (item.label.toLowerCase().startsWith(prefix)) {
            this.controller.focusItem(item.index);
            window.requestAnimationFrame(() => this.scrollToItemIfNeeded());
            return;
          }
        }
      }
    }
  }

  focusItemWithLabel(label) {
    label = label.toLowerCase();
    const sections = this.controller.getDisplaySections();
    for (const section of sections) {
      if (section.items) {
        for (const item of section.items) {
          if (item.label.toLowerCase() === label) {
            this.controller.focusItem(item.index);
            window.requestAnimationFrame(() => this.scrollToItemIfNeeded());
            return;
          }
        }
      }
    }
  }

  focusSelectedItem() {
    const sections = this.controller.getDisplaySections(true);
    for (const section of sections) {
      if (section.items) {
        for (const item of section.items) {
          if (item.isSelected) {
            this.controller.focusItem(item.index);
            window.requestAnimationFrame(() => this.scrollToItemIfNeeded());
            return;
          }
        }
      }
    }
  }

  focus() {
    this.el.querySelector(`.screen-list-container`).focus();
  }
}

export default registerMPElement(`mp-items-menu`, ItemsMenu);
